import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  getAllSecretsRequest,
  postSecretRequest,
  updateSecretRequest,
} from "./secretsApiService";
import { RootState } from "@app/store";
import { ISecret, SecretRequest } from "./types";

//TODO replace all "any" with correct ISecret type

const secretsAdapter = createEntityAdapter<ISecret>({
  selectId: (secret) => secret.secret_id,
});

const initialState = secretsAdapter.getInitialState({
  status: "",
  selectedSecret: "",
  selectedResource: "",
});

const secretsSlice = createSlice({
  name: "secrets",
  initialState,
  reducers: {
    selectSecret(state, action: PayloadAction<string>) {
      state.selectedSecret = action.payload;
    },
    selectResource(state, action: PayloadAction<string>) {
      state.selectedResource = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSecrets.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchSecrets.fulfilled,
        (state, { payload }: PayloadAction<ISecret[]>) => {
          secretsAdapter.setAll(state, payload);
          state.status = "";
        },
      )
      .addCase(fetchSecrets.rejected, (state) => {
        state.status = "";
      })
      .addCase(updateSecret.pending, (state) => {
        state.status = "submitting";
      })
      .addCase(
        updateSecret.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          secretsAdapter.upsertOne(state, payload);
          state.status = "";
        },
      )
      .addCase(updateSecret.rejected, (state) => {
        state.status = "";
      })
      .addCase(createSecret.pending, (state) => {
        state.status = "submitting";
      })
      .addCase(
        createSecret.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          secretsAdapter.upsertOne(state, payload);
          state.status = "";
        },
      )
      .addCase(createSecret.rejected, (state) => {
        state.status = "";
      });
  },
});

// TODO: later integrate better with Toasts?
export const fetchSecrets = createAsyncThunk(
  "secrets/fetchSecrets",
  async (customerId: string) => {
    try {
      const res = await getAllSecretsRequest(customerId);
      if (res) {
        return res.data;
      } else return res;
    } catch (err) {
      console.error(err);
      throw new Error("Failed to fetch secret data");
    }
  },
);

export interface SecretUpdateBundle {
  customerId: string;
  secretName: string;
  secretResource: string;
  payload: SecretRequest;
}

export const updateSecret = createAsyncThunk(
  "secrets/updateSecret",
  async (data: SecretUpdateBundle) => {
    try {
      const res = await updateSecretRequest(
        data.customerId,
        data.secretName,
        data.secretResource,
        data.payload,
      );
      return res?.data ?? res;
    } catch (err) {
      console.error(err);
      throw new Error("Failed to update secret");
    }
  },
);

export interface SecretCreateBundle {
  customerId: string;
  secretResource: string;
  payload: SecretRequest;
}

export const createSecret = createAsyncThunk(
  "secrets/createSecret",
  async (data: SecretCreateBundle) => {
    try {
      const res = await postSecretRequest(
        data.customerId,
        data.secretResource,
        data.payload,
      );
      return res?.data ?? res;
    } catch (err) {
      console.error(err);
      throw new Error("Failed to add secret");
    }
  },
);

export const { selectSecret, selectResource } = secretsSlice.actions;
export default secretsSlice.reducer;

const secretsSelectors = secretsAdapter.getSelectors(
  (state: RootState) => state.secrets,
);

// Selector for getting customer's tasks from the state
export const getAllSecrets = (state: RootState) => {
  return secretsSelectors.selectAll(state);
};

export const getAllSecretsByResource = (state: RootState) => {
  const selectedResource = state.secrets.selectedResource;
  return secretsSelectors
    .selectAll(state)
    .filter((secret: any) => secret.location.resource === selectedResource);
};

export const getSelectedSecretId = (state: RootState) => {
  return state.secrets.selectedSecret;
};

export const getSelectedSecret = (state: RootState) => {
  return secretsSelectors.selectById(state, state.secrets.selectedSecret);
};

export const getSelectedResource = (state: RootState) => {
  return state.secrets.selectedResource;
};

export const selectSecretsStatus = (state: RootState) => state.secrets.status;

export const getSecretResources = (state: RootState) => {
  const secrets = secretsSelectors.selectAll(state);
  const uniqueLocations = new Map<string, any>();

  secrets.forEach((secret: any) => {
    if (secret.location) {
      const key = `${secret.location.resource}-${secret.location.cloud_platform}`;
      if (!uniqueLocations.has(key)) {
        uniqueLocations.set(key, secret.location);
      }
    }
  });

  return Array.from(uniqueLocations.values());
};
